/** @format */

import React, { FC, useState, KeyboardEvent } from "react";
import styled from "styled-components";
import { SwitchProps } from "./Switch.types";

const Wrapper = styled.div<{ disabled?: boolean }>`
  opacity: ${({ disabled }) => disabled && 0.5};
  height: 20px;
  display: flex;
  align-items: center;
  position: relative;
`;

const SwitchInput = styled.input`
  position: absolute;
  opacity: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  cursor: pointer;
  margin: 0;
  
  &:focus + .switch-label {
    outline: 2px solid #4d90fe;
    outline-offset: 1px;
  }
  
  &:disabled {
    cursor: not-allowed;
  }
`;

const SwitchLabel = styled.label<SwitchProps>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  width: 34px;
  height: 20px;
  border-radius: 50px;
  border: 2px solid
    ${({ theme, toggled }) =>
      toggled ? theme.colors.signal.greenDark : theme.colors.brown.brown4tra};
  background-color: ${({ theme, toggled }) =>
    toggled ? theme.colors.palette.success : theme.colors.palette["shade-600"]};
  position: relative;
  transition: background-color 0.2s;
`;

const SwitchButton = styled.span`
  content: "";
  position: absolute;
  top: 0px;
  left: 0px;
  width: 16px;
  height: 16px;
  border-radius: 10px;

  transition: 0.2s;
  background: ${({ theme }) => theme.colors.white.white100};
  box-shadow: 0px 4px 10px rgba(186, 160, 79, 0.5);
  ${SwitchInput}:checked + ${SwitchLabel} & {
    left: 100%;
    transform: translateX(-100%);
  }
  ${SwitchLabel}:active & {
    width: 20px;
  }
`;

const Switch: FC<SwitchProps> = ({
  id = "switch",
  toggled,
  receiveValue,
  disabled,
  className
}) => {
  const [toggle, setToggle] = useState(false);

  const handleToggle = () => {
    if (disabled) return;
    
    setToggle(!toggle);
    // Pass the new toggled state as a string value
    receiveValue?.(!toggled ? "true" : "false");
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    // Toggle on Space or Enter key press
    if (e.key === " " || e.key === "Enter") {
      e.preventDefault(); // Prevent page scroll on space
      handleToggle();
    }
  };

  // Prevent focus on mouse interaction
  const preventFocus = (e: React.MouseEvent) => {
    e.preventDefault();
  };

  return (
    <Wrapper disabled={disabled} className={className}>
      <SwitchInput
        className="switch-checkbox"
        id={id}
        type="checkbox"
        checked={!!toggled}
        disabled={disabled}
        onChange={() => handleToggle()}
        onKeyDown={handleKeyDown}
        onMouseDown={preventFocus}
        aria-checked={!!toggled}
        role="switch"
      />
      <SwitchLabel
        toggled={toggled}
        className="switch-label"
        htmlFor={id}
      >
        <SwitchButton
          id={`${id}-switch-button`}
          className="switch-button"
        />
      </SwitchLabel>
    </Wrapper>
  );
};

export default Switch;
